// src/slices/roleManagement/thunk.ts
// Async thunks for Role Management

import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  getRoleGroups,
  getRoleGroupById,
  createRoleGroup,
  updateRoleGroup,
  deleteRoleGroup,
  getModules,
  getPermissions,
  getPermissionsByModule,
} from 'helpers/fakebackend_helper';
import {
  CreateRoleGroupRequest,
  UpdateRoleGroupRequest,
} from 'pages/RoleManagement/types';

interface GetRoleGroupsParams {
  pageIndex?: number;
  pageSize?: number;
  search?: string;
  isActive?: boolean;
  sortBy?: string;
  sortOrder?: string;
}

// Fetch role groups list
export const fetchRoleGroups = createAsyncThunk(
  'RoleManagement/fetchRoleGroups',
  async (params: GetRoleGroupsParams = {}, { rejectWithValue }) => {
    try {
      const response = await getRoleGroups(params);
      return response.data || response;
    } catch (error: any) {
      return rejectWithValue(
        error?.message || 'Có lỗi xảy ra khi tải danh sách nhóm quyền'
      );
    }
  }
);

// Fetch single role group with full details (including permissions)
export const fetchRoleGroupDetail = createAsyncThunk(
  'RoleManagement/fetchRoleGroupDetail',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await getRoleGroupById(id);
      return response.data || response;
    } catch (error: any) {
      return rejectWithValue(
        error?.message || 'Có lỗi xảy ra khi tải chi tiết nhóm quyền'
      );
    }
  }
);

// Fetch modules
export const fetchModules = createAsyncThunk(
  'RoleManagement/fetchModules',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getModules();
      return response.data || response;
    } catch (error: any) {
      return rejectWithValue(
        error?.message || 'Có lỗi xảy ra khi tải danh sách module'
      );
    }
  }
);

// Fetch all permissions
export const fetchPermissions = createAsyncThunk(
  'RoleManagement/fetchPermissions',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getPermissions();
      return response.data || response;
    } catch (error: any) {
      return rejectWithValue(
        error?.message || 'Có lỗi xảy ra khi tải danh sách quyền'
      );
    }
  }
);

// Fetch permissions by module
export const fetchPermissionsByModule = createAsyncThunk(
  'RoleManagement/fetchPermissionsByModule',
  async (_moduleId: number | undefined = undefined, { rejectWithValue }) => {
    try {
      // Note: getPermissionsByModule doesn't take moduleId param
      // Server will return all permissions, filter by moduleId on client if needed
      const response = await getPermissionsByModule();
      return response.data || response;
    } catch (error: any) {
      return rejectWithValue(
        error?.message || 'Có lỗi xảy ra khi tải quyền theo module'
      );
    }
  }
);

// Create new role group
export const createNewRoleGroup = createAsyncThunk(
  'RoleManagement/createRoleGroup',
  async (data: CreateRoleGroupRequest, { rejectWithValue }) => {
    try {
      const response = await createRoleGroup(data);
      return response.data || response;
    } catch (error: any) {
      return rejectWithValue(
        error?.message || 'Có lỗi xảy ra khi tạo nhóm quyền'
      );
    }
  }
);

// Update existing role group
export const updateExistingRoleGroup = createAsyncThunk(
  'RoleManagement/updateRoleGroup',
  async (
    { id, data }: { id: number; data: UpdateRoleGroupRequest },
    { rejectWithValue }
  ) => {
    try {
      const response = await updateRoleGroup(id, data);
      return response.data || response;
    } catch (error: any) {
      return rejectWithValue(
        error?.message || 'Có lỗi xảy ra khi cập nhật nhóm quyền'
      );
    }
  }
);

// Delete role group
export const deleteRoleGroupById = createAsyncThunk(
  'RoleManagement/deleteRoleGroup',
  async (id: number, { rejectWithValue }) => {
    try {
      await deleteRoleGroup(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(
        error?.message || 'Có lỗi xảy ra khi xóa nhóm quyền'
      );
    }
  }
);
